import {FC, Fragment} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import Button from 'components/Button';
import SubmitButton from 'components/Form/SubmitButton';
import Layout from 'components/Layout';
import {setUrl} from 'utils/request';
import {onApiFormError} from 'utils/request/result';
import {usePostDelete} from '../services';
import {
    DeletionSubmissionFormData,
    EmployeeUserAccountToDelete,
    PartnersToBeDeleted,
} from '../types';

export type DeletionConfirmationProps = {
    formData: DeletionSubmissionFormData;
    onCancel: () => void;
    previewData: {
        allPartnersToDelete: Array<PartnersToBeDeleted>;
        employeeUserAccountsToDelete: Array<EmployeeUserAccountToDelete>;
        willDeleteUser: boolean;
    };
};

const DeletionConfirmation: FC<DeletionConfirmationProps> = ({
    formData,
    onCancel,
    previewData: {
        allPartnersToDelete,
        employeeUserAccountsToDelete,
        willDeleteUser,
    },
}) => {
    const methods = useForm({
        shouldUnregister: true,
    });
    const {handleSubmit} = methods;

    const {mutateAsync} = usePostDelete();

    const {t} = useTranslation('Profile.DeletionConfirm.DeletionConfirmation');

    const onSubmit = async () => {
        await mutateAsync(formData)
            .then(() => {
                setUrl(window.Urls.overview_view());
            })
            .catch(onApiFormError(methods));
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Layout.Stacked className="max-w-xl">
                    <p className="font-bold">{t('message')}</p>
                    {willDeleteUser && (
                        <>
                            <p>{t('yourUserAccount')}</p>
                            <ul>
                                <li>{t('noLongerAbleToUseMakeLeaps')}</li>
                            </ul>
                        </>
                    )}
                    {!!employeeUserAccountsToDelete?.length && (
                        <>
                            <p>{t('teamMemberAccounts')}</p>
                            <ul>
                                {employeeUserAccountsToDelete.map((account) => (
                                    <li key={account.email}>
                                        {account.firstName} {account.lastName} (
                                        {account.email})
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                    {allPartnersToDelete?.map((account) => (
                        <Fragment key={account.mid}>
                            <p>
                                <Trans
                                    i18nKey="yourCompany"
                                    ns="Profile.DeletionConfirm.DeletionConfirmation"
                                    t={t}
                                    values={{
                                        company: account.name,
                                    }}
                                />
                            </p>
                            <ul>
                                {account.receiveddocumentsCount > 0 && (
                                    <li>
                                        <Trans
                                            i18nKey="receivedDocuments"
                                            ns="Profile.DeletionConfirm.DeletionConfirmation"
                                            t={t}
                                            values={{
                                                count: account.receiveddocumentsCount,
                                            }}
                                        />
                                    </li>
                                )}
                                {account.documentCount > 0 && (
                                    <li>
                                        <Trans
                                            i18nKey="createdDocuments"
                                            ns="Profile.DeletionConfirm.DeletionConfirmation"
                                            t={t}
                                            values={{
                                                count: account.documentCount,
                                            }}
                                        />
                                    </li>
                                )}
                                {account.activeEmployeeCount > 0 && (
                                    <li>
                                        <Trans
                                            i18nKey="teamMembers"
                                            ns="Profile.DeletionConfirm.DeletionConfirmation"
                                            t={t}
                                            values={{
                                                count: account.activeEmployeeCount,
                                            }}
                                        />
                                    </li>
                                )}
                                {account.activeClientCount > 0 && (
                                    <li>
                                        <Trans
                                            i18nKey="clients"
                                            ns="Profile.DeletionConfirm.DeletionConfirmation"
                                            t={t}
                                            values={{
                                                count: account.activeClientCount,
                                            }}
                                        />
                                    </li>
                                )}
                            </ul>
                        </Fragment>
                    ))}
                    <div className="flex justify-end">
                        <Button
                            className="mr-8"
                            kind="tertiary"
                            onClick={onCancel}
                        >
                            {t('form:cancel')}
                        </Button>
                        <SubmitButton kind="destructive">
                            {t('submitButton')}
                        </SubmitButton>
                    </div>
                </Layout.Stacked>
            </form>
        </FormProvider>
    );
};

export default DeletionConfirmation;
